import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { CalendarView } from './JRCCalendar';
import { JRCTypography } from '../JRCTypography/JRCTypography';
import { useCallback, useMemo } from 'react';
import { Audience, JCalendarApi, JGlobalApi, UserResponseStatus } from 'jamespot-user-api';
import { JRCAvatars } from '../JRCAvatars/JRCAvatars';
import { useIntl } from 'react-intl';
import { JRCFlexBox } from '../JRCFlex/JRCFlexBox';
import { JRCTooltip } from '../JRCTooltip/JRCTooltip';
import { getDarkShade, getLightShade } from '../../styles/utils';
import { JRCImg } from '../JRCImg/JRCImg';
import { JRCEventStatusButton, SubscriptionModifiers } from './JRCEventStatusButton';

export type JRCCalendarEventProps = {
    id: string;
    title: string;
    start: Date | null;
    end: Date | null;
    status: UserResponseStatus | null;
    display: CalendarView;
    onClick: (id: number) => void;
    audience: Pick<Audience, '_url' | 'title' | 'uri'>[];
    allDay?: boolean;
    textColor?: string;
    backgroundColor?: string;
    address?: string;
    image: boolean;
    type: string;
    isVisio?: boolean;
    handler: {
        global: Pick<JGlobalApi, 'updateAction' | 'removeAction'>;
        calendar: Pick<JCalendarApi, 'reviveSubmit'>;
    };
    onError: (errMsg?: string) => void;
    onSuccess: (message: string) => void;
    noGestion: boolean;
    setSubscriptionModifiers: (fn: (prev: SubscriptionModifiers) => SubscriptionModifiers) => void;
    uri: string;
    canSubscribe: boolean;
    subscriptionModifiers: SubscriptionModifiers;
};

const MonthContainer = styled.button<{ backgroundColor?: string; variant: 'outlined' | 'filled' }>`
    display: flex;
    align-items: center;
    padding: ${({ theme }) => theme.size.xs}px ${({ theme }) => theme.size.sm}px ${({ theme }) => theme.size.xs}px
        ${({ theme, variant }) => (variant === 'outlined' ? 12 : theme.size.sm)}px;
    height: calc(100% - ${({ variant }) => (variant === 'outlined' ? 2 : 0)}px);
    width: calc(100% - ${({ variant }) => (variant === 'outlined' ? 2 : 0)}px);
    border-radius: ${({ theme }) => theme.size.xs}px;
    background-color: ${({ backgroundColor, variant, theme }) =>
        variant === 'outlined' ? theme.color.white : backgroundColor || theme.color.grey1};
    ${({ variant }) =>
        variant === 'outlined' &&
        `
        margin-top: 1px;
        margin-left: 1px;
    `}
    border: 1px solid ${({ theme, variant, backgroundColor }) =>
        variant === 'outlined' ? backgroundColor || theme.color.grey1 : theme.color.white};

    &:hover {
        background-color: ${({ theme, backgroundColor, variant }) =>
            variant === 'outlined'
                ? backgroundColor
                    ? getLightShade(backgroundColor, 0.9)
                    : theme.color.grey0
                : backgroundColor
                ? getDarkShade(backgroundColor, 0.05)
                : theme.color.grey2};
    }

    ${({ variant, backgroundColor, theme }) =>
        variant === 'outlined' &&
        `&:before {
        content: '';
        width: 8px;
        border-radius: ${theme.size.xs}px 0 0 ${theme.size.xs}px;
        background-color: ${backgroundColor || theme.color.grey1};
        position: absolute;
        left: 2px;
        top: 2px;
        bottom: 1px;
    }`}
`;

const WeekContainer = styled.button<{
    backgroundColor?: string;
    variant: 'outlined' | 'filled';
    size: 'xs' | 's' | 'l';
}>`
    display: flex;
    flex-direction: ${({ size }) => (size === 'l' ? 'column' : 'row')};
    padding: 2px 6px 2px ${({ variant }) => (variant === 'outlined' ? 12 : 6)}px;
    width: calc(100% - ${({ variant }) => (variant === 'outlined' ? 4 : 0)}px);
    background-color: ${({ color }) => color};
    height: calc(100% - ${({ variant }) => (variant === 'outlined' ? 4 : 1)}px);
    ${({ variant }) =>
        variant === 'outlined' &&
        `
        margin-top: 1px;
        margin-left: 2px;
    `}
    border-radius: ${({ theme }) => theme.size.sm}px;
    background-color: ${({ backgroundColor, variant, theme }) =>
        variant === 'outlined' ? theme.color.white : backgroundColor || theme.color.grey1};
    border: 2px solid
        ${({ theme, variant, backgroundColor }) =>
            variant === 'outlined' ? backgroundColor || theme.color.grey1 : theme.color.white};

    &:hover {
        border: 2px solid
            ${({ theme, variant, backgroundColor }) =>
                variant === 'outlined' ? backgroundColor || theme.color.grey1 : theme.color.white};
        background-color: ${({ theme, backgroundColor, variant }) =>
            variant === 'outlined'
                ? backgroundColor
                    ? getLightShade(backgroundColor, 0.9)
                    : theme.color.grey0
                : backgroundColor
                ? getDarkShade(backgroundColor, 0.05)
                : theme.color.grey2};
    }

    ${({ variant, backgroundColor, theme }) =>
        variant === 'outlined' &&
        `&:before {
        content: '';
        width: 8px;
        border-radius: ${theme.size.sm}px 0 0 ${theme.size.sm}px;
        background-color: ${backgroundColor || theme.color.grey1};
        position: absolute;
        left: 3px;
        top: 3px;
        bottom: 3px;
    }`}
`;

const ListContainer = styled(WeekContainer)`
    padding: ${({ theme }) => theme.space.sm}px ${({ theme }) => theme.space.sm}px ${({ theme }) => theme.space.sm}px
        ${({ theme, variant }) => (variant === 'outlined' ? 24 : theme.space.sm)}px;
    flex-direction: row;
    justify-content: space-between;
    border: 2px solid ${({ theme, backgroundColor }) => backgroundColor || theme.color.grey1};
    width: 100%;
    height: 100%;
    margin-top: 0;
    margin-left: 0;
    position: relative;

    &:hover {
        border: 2px solid ${({ theme, backgroundColor }) => backgroundColor || theme.color.grey1};
        background-color: ${({ theme, backgroundColor, variant }) =>
            variant === 'outlined'
                ? backgroundColor
                    ? getLightShade(backgroundColor, 0.9)
                    : theme.color.grey0
                : backgroundColor
                ? getDarkShade(backgroundColor, 0.05)
                : theme.color.grey2};
    }

    ${({ variant, backgroundColor, theme }) =>
        variant === 'outlined' &&
        `&:before {
        content: '';
        width: 16px;
        border-radius: ${theme.size.sm}px 0 0 ${theme.size.sm}px;
        background-color: ${backgroundColor || theme.color.grey1};
        position: absolute;
        left: -2px;
        top: -2px;
        bottom: -2px;
    }`}
`;

const ListSubContainer = styled.div``;

const Start = styled(JRCTypography)<{
    size: 'xs' | 's' | 'l';
    textColor?: string;
    displayVariant: 'outlined' | 'filled';
}>`
    margin-right: 4px;
    font-size: ${({ size }) => (size === 'l' ? '12px' : size === 's' ? '11px' : '0')};
    color: ${({ theme, textColor, displayVariant }) =>
        displayVariant === 'outlined' || !textColor ? theme.font.color : textColor};
`;

const ListText = styled(JRCTypography)<{ textColor?: string; displayVariant: 'outlined' | 'filled' }>`
    font-size: 12px;
    color: ${({ theme, textColor, displayVariant }) =>
        displayVariant === 'outlined'
            ? theme.color.grey5
            : textColor
            ? getLightShade(textColor, 0.1)
            : theme.color.grey5};
`;

const Title = styled.p<{ lineThrough: boolean; textColor?: string; variant: 'outlined' | 'filled' }>`
    font-weight: ${({ theme }) => theme.font.weight.medium};
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 12px;
    text-wrap: nowrap;
    color: ${({ theme, textColor, variant }) =>
        variant === 'outlined' ? theme.font.color : textColor || theme.font.color};
    text-decoration: ${({ lineThrough }) => (lineThrough ? 'line-through' : 'none')};
`;

const GridTitle = styled(Title)<{ size: 'xs' | 's' | 'l' }>`
    font-size: ${({ size }) => (size === 'l' ? '12px' : size === 's' ? '11px' : '0')};
`;

const ListTitle = styled(Title)<{ image: boolean }>`
    font-size: 14px;
    max-width: ${({ image }) => 500 - (image ? 80 : 0)}px;

    @media (max-width: 1100px) {
        max-width: ${({ image }) => 400 - (image ? 80 : 0)}px;
    }

    @media (max-width: 950px) {
        max-width: ${({ image }) => 300 - (image ? 80 : 0)}px;
    }

    @media (max-width: 850px) {
        max-width: ${({ image }) => 200 - (image ? 80 : 0)}px;
    }
`;

const ImageWrapper = styled.div`
    border-radius: ${({ theme }) => theme.size.xs}px;
    display: inline-flex;
    overflow: hidden;
`;

const GridTitleWrapper = styled(JRCFlexBox)`
    overflow: hidden;
`;

const GridDate = styled(JRCFlexBox)`
    flex-shrink: 0;
`;

export const JRCCalendarEvent = ({
    status,
    title,
    start,
    end,
    display,
    id: stringId,
    onClick,
    audience,
    allDay,
    textColor,
    backgroundColor,
    address,
    image,
    type,
    isVisio = false,
    handler,
    onError,
    onSuccess,
    noGestion,
    setSubscriptionModifiers,
    uri,
    canSubscribe,
    subscriptionModifiers,
}: JRCCalendarEventProps) => {
    const intl = useIntl();
    const theme = useTheme();

    const displayedStatus = useMemo(
        () => subscriptionModifiers.find((s) => s.uri === uri)?.status ?? status,
        [status, uri, subscriptionModifiers],
    );
    const id = useMemo(() => Number(stringId) ?? 0, [stringId]);
    const duration = useMemo(
        () => (start && end ? Math.ceil((end.getTime() - start.getTime()) / 60000) : undefined),
        [end, start],
    );
    const size = useMemo(() => {
        if (!duration) return 'l';
        if (duration >= 75) {
            return 'l';
        } else if (duration >= 40) {
            return 's';
        } else {
            return 'xs';
        }
    }, [duration]);

    const getColorEmoji = useCallback((status: UserResponseStatus | null) => {
        switch (status) {
            case 'available':
            case 'visio':
            case 'onSpot':
            case 1:
                return '🟢';
            case 'unknown':
                return '🟡';
            case 0:
            case 'unavailable':
                return '🔴';
            default:
                return '🟢';
        }
    }, []);

    const startDisplay = useMemo(
        () =>
            allDay
                ? intl.formatMessage({ id: 'CALENDAR_All_Day' })
                : start?.getHours() + ':' + ('0' + start?.getMinutes()).slice(-2),
        [allDay, intl, start],
    );

    const isOnSeveralDays = useMemo(
        () =>
            start &&
            end &&
            (start.getDate() !== end.getDate() ||
                start.getMonth() !== start.getMonth() ||
                start.getFullYear() !== start.getFullYear()),
        [end, start],
    );

    const variant = useMemo(() => (displayedStatus === 'unknown' ? 'outlined' : 'filled'), [displayedStatus]);

    const doOnClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onClick(id);
        },
        [id, onClick],
    );

    if (display === 'dayGridMonth') {
        return (
            <MonthContainer backgroundColor={backgroundColor} variant={variant} onClick={doOnClick}>
                {start && !allDay && (
                    <Start textColor={textColor} size={'l'} displayVariant={variant}>
                        {startDisplay}
                    </Start>
                )}
                <Title textColor={textColor} lineThrough={displayedStatus === 'unavailable'} variant={variant}>
                    {title}
                </Title>
            </MonthContainer>
        );
    }

    if (display === 'timeGridWeek' || display === 'timeGridDay') {
        return (
            <JRCTooltip
                title={startDisplay + ' ' + getColorEmoji(displayedStatus)}
                position={'top'}
                description={title}
                asBlock={true}
                active={!!duration && duration < 30}>
                <WeekContainer backgroundColor={backgroundColor} variant={variant} size={size} onClick={doOnClick}>
                    {start && !allDay && (
                        <GridDate y={'center'} gap={2}>
                            <Start textColor={textColor} size={size} displayVariant={variant}>
                                {isOnSeveralDays &&
                                    `${('0' + start.getDate()).slice(-2)}/${('0' + (start.getMonth() + 1)).slice(-2)} `}
                                {startDisplay}
                            </Start>
                        </GridDate>
                    )}
                    <GridTitleWrapper y={'center'} gap={2}>
                        <GridTitle
                            textColor={textColor}
                            lineThrough={displayedStatus === 'unavailable'}
                            size={size}
                            variant={variant}>
                            {title}
                        </GridTitle>
                    </GridTitleWrapper>
                </WeekContainer>
            </JRCTooltip>
        );
    }

    if (display === 'list') {
        return (
            <ListContainer size={size} backgroundColor={backgroundColor} variant={variant} onClick={doOnClick}>
                <JRCFlexBox gap={18} y={'center'}>
                    {image && (
                        <ImageWrapper>
                            <JRCImg
                                width={60}
                                aspectRatio={1}
                                alt={title}
                                type={type}
                                recordId={id}
                                size={'original'}
                                from={'imagecache'}
                                objectFit={'cover'}
                            />
                        </ImageWrapper>
                    )}
                    <ListSubContainer>
                        <ListTitle
                            textColor={textColor}
                            lineThrough={displayedStatus === 'unavailable'}
                            variant={variant}
                            image={!!image}>
                            {title}
                        </ListTitle>
                        {start && (
                            <ListText textColor={textColor} displayVariant={variant}>
                                {isOnSeveralDays &&
                                    `${('0' + start.getDate()).slice(-2)}/${('0' + (start.getMonth() + 1)).slice(-2)} `}
                                {(!isOnSeveralDays || !allDay) && startDisplay}
                                {end && (isOnSeveralDays || !allDay) && ' - '}
                                {isOnSeveralDays &&
                                    end &&
                                    `${('0' + end.getDate()).slice(-2)}/${('0' + (end.getMonth() + 1)).slice(-2)} `}
                                {end && !allDay && `${end.getHours()}:${('0' + end.getMinutes()).slice(-2)}`}
                            </ListText>
                        )}
                        {address && (
                            <ListText textColor={textColor} displayVariant={variant}>
                                {address}
                            </ListText>
                        )}
                    </ListSubContainer>
                </JRCFlexBox>
                <JRCFlexBox y={'center'} gap={theme.space.sm}>
                    <JRCAvatars audience={audience} />
                    {!noGestion && (
                        <JRCEventStatusButton
                            eventId={id}
                            onError={onError}
                            handler={handler}
                            eventIsVisio={isVisio}
                            eventType={type}
                            initialStatus={status}
                            onSuccess={onSuccess}
                            setSubscriptionModifiers={setSubscriptionModifiers}
                            eventUri={uri}
                            canSubscribe={canSubscribe}
                            status={displayedStatus}
                        />
                    )}
                </JRCFlexBox>
            </ListContainer>
        );
    }

    return null;
};
